/**
 * Error Page
 *
 * Custom error page for 404 and 500 errors.
 * Provides friendly messages and a link back to home.
 * Does NOT show technical stack traces.
 */

import { Link, useRouteError } from "react-router-dom";
import { Home, AlertCircle, Frown } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorPageProps {
  error?: {
    status?: number;
    message?: string;
  };
}

export function ErrorPage({ error }: ErrorPageProps) {
  // If used as a React Router error element, extract error info from the route
  const routeError = useRouteError() as
    | { status?: number; message?: string }
    | undefined;

  const status = error?.status ?? routeError?.status ?? 404;
  const message = error?.message ?? routeError?.message;

  const is404 = status === 404;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Icon */}
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-muted mx-auto">
          {is404 ? (
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          ) : (
            <Frown className="h-8 w-8 text-muted-foreground" />
          )}
        </div>

        {/* Status code and heading */}
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-foreground">{status}</h1>
          <h2 className="text-xl font-semibold text-foreground">
            {is404 ? "Page not found" : "Something went wrong"}
          </h2>
          <p className="text-muted-foreground">
            {is404
              ? "The page you're looking for doesn't exist or has been moved."
              : "We're having trouble loading this page. Please try again later."}
          </p>
          {message && !is404 && (
            <p className="text-sm text-muted-foreground mt-2">{message}</p>
          )}
        </div>

        {/* Back to home button */}
        <Button asChild className="gap-2">
          <Link to="/">
            <Home className="h-4 w-4" />
            Back to Home
          </Link>
        </Button>
      </div>
    </div>
  );
}
